import DOMPurify from 'dompurify';
import { marked } from 'marked';

// Configure marked with basic settings
marked.setOptions({
  gfm: true,
  breaks: false,
  pedantic: false
});

// Configure marked with custom renderer
marked.use({
  renderer: {
    heading({ tokens, depth }) {
      const text = this.parser.parseInline(tokens);
      const id = text.toLowerCase().replace(/[^\w]+/g, '-');
      const marginTop = depth <= 2 ? '1.5em' : '1em';
      const marginBottom = depth <= 2 ? '0.5em' : '0.5em';
      const fontSize = {
        1: '2em',
        2: '1.5em',
        3: '1.2em',
        4: '1.1em',
        5: '1em',
        6: '0.9em'
      }[depth] || '1em';
      
      return `<h${depth} id="${id}" style="font-size: ${fontSize}; margin-top: ${marginTop}; margin-bottom: ${marginBottom}; font-weight: 600; line-height: 1.25; color: inherit;">${text}</h${depth}>\n`;
    },

    paragraph({ tokens }) {
      const text = this.parser.parseInline(tokens);
      return `<p style="margin: 0.8em 0; line-height: 1.6; color: inherit;">${text}</p>\n`;
    },

    list(token) {
      const type = token.ordered ? 'ol' : 'ul';
      const startatt = (token.ordered && token.start !== 1) ? ` start="${token.start}"` : '';
      const listStyle = token.ordered ? 'decimal' : 'disc';
      const body = token.items.map(item => this.listitem(item)).join('');
      return `<${type}${startatt} style="padding-left: 1.5em; margin: 0.5em 0; list-style-type: ${listStyle}; color: inherit;">\n${body}</${type}>\n`;
    },

    listitem(item) {
      if (item.task) {
        const checkedAttr = item.checked ? ' checked=""' : '';
        const text = this.parser.parse(item.tokens);
        return `<li style="margin: 0.3em 0; list-style: none; color: inherit;"><input type="checkbox"${checkedAttr} disabled="" style="margin-right: 0.5em;"> ${text}</li>\n`;
      }
      const text = this.parser.parse(item.tokens);
      return `<li style="margin: 0.3em 0; color: inherit;">${text}</li>\n`;
    },

    strong({ tokens }) {
      const text = this.parser.parseInline(tokens);
      return `<strong style="font-weight: 600; color: inherit;">${text}</strong>`;
    },

    em({ tokens }) {
      const text = this.parser.parseInline(tokens);
      return `<em style="font-style: italic; color: inherit;">${text}</em>`;
    },

    code({ text, lang }) {
      const escapedText = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<pre style="margin: 1rem 0; padding: 1rem; border-radius: 0.5rem; background-color: #0d1117; overflow-x: auto; border: 1px solid rgba(75, 85, 99, 0.3);"><code class="language-${lang || ''}" style="font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; background-color: transparent; padding: 0; border-radius: 0; font-size: 0.875rem; line-height: 1.5; color: #e6edf3;">${escapedText}</code></pre>\n`;
    },

    codespan({ text }) {
      return `<code style="background-color: rgba(55, 65, 81, 0.5); padding: 0.125rem 0.25rem; border-radius: 0.25rem; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; font-size: 0.875em;">${text}</code>`;
    },

    blockquote({ tokens }) {
      const text = this.parser.parse(tokens);
      return `<blockquote style="border-left: 4px solid #4b5563; padding-left: 1rem; margin: 1.5rem 0; color: #9ca3af; font-style: italic;">\n${text}</blockquote>\n`;
    },

    table(token) {
      const header = token.header.map(cell => this.tablecell(cell)).join('');
      const body = token.rows.map(row => {
        const cells = row.map(cell => this.tablecell(cell)).join('');
        return `<tr style="border-bottom: 1px solid #4b5563;">${cells}</tr>\n`;
      }).join('');
      return `<table style="border-collapse: collapse; width: 100%; margin: 1rem 0; border: 1px solid #4b5563;">
        <thead style="background-color: rgba(55, 65, 81, 0.5);"><tr>${header}</tr></thead>
        <tbody>${body}</tbody>
      </table>\n`;
    },

    tablecell(token) {
      const content = this.parser.parseInline(token.tokens);
      const type = token.header ? 'th' : 'td';
      const align = token.align ? ` style="text-align: ${token.align}; padding: 0.5rem;"` : ' style="padding: 0.5rem;"';
      return `<${type}${align}>${content}</${type}>\n`;
    }
  }
});

/**
 * Professional Markdown to HTML converter with proper styling and citation support
 */
export function convertMarkdownToHtml(markdown: string): string {
  if (!markdown || typeof markdown !== 'string' || !markdown.trim()) {
    return '';
  }

  try {

    // Protect citation markers from markdown processing
    const citationPlaceholders = new Map<string, string>();
    let protectedText = markdown;

    // Replace citation markers with placeholders that won't be affected by markdown
    // Handle both [Source N] format (new) and [N] format (legacy)
    protectedText = protectedText.replace(/\[Source\s*(\d+)\]/gi, (match, num) => {
      const placeholder = `CITATIONMARKER${num}CITATIONMARKER`;
      citationPlaceholders.set(placeholder, `[Source ${num}]`);
      return placeholder;
    });
    protectedText = protectedText.replace(/\[(\d+)\]/g, (match, num) => {
      const placeholder = `CITATIONMARKER${num}CITATIONMARKER`;
      if (!citationPlaceholders.has(placeholder)) {
        citationPlaceholders.set(placeholder, match);
      }
      return placeholder;
    });

    // Convert markdown to HTML using marked
    let html = marked.parse(protectedText) as string;

    // Restore citation markers
    citationPlaceholders.forEach((originalMarker, placeholder) => {
      // Replace all occurrences of the placeholder back to the original marker
      html = html.split(placeholder).join(originalMarker);
    });


    // Post-processing cleanup
    html = cleanupHtml(html);

    // Sanitize the HTML
    const sanitizedHtml = DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      ADD_TAGS: ['cite', 'details', 'summary', 'span'],
      ADD_ATTR: ['style', 'target', 'rel', 'class', 'data-citation-index', 'data-source-number', 'title', 'onmouseover', 'onmouseout'],
      ALLOW_DATA_ATTR: true,
      ALLOW_UNKNOWN_PROTOCOLS: false
    });

    return sanitizedHtml.trim();
  } catch (error) {
    console.error('[markdownRenderer] Error converting markdown:', error);
    // Fallback to escaped plain text
    return DOMPurify.sanitize(
      markdown
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
    );
  }
}

/**
 * Clean up the HTML output for better professional appearance
 */
function cleanupHtml(html: string): string {
  // Remove empty paragraphs
  html = html.replace(/<p>\s*<\/p>/gi, '');
  
  // Remove empty headings
  html = html.replace(/<h[1-6]>\s*<\/h[1-6]>/gi, '');
  
  // Clean up excessive line breaks
  html = html.replace(/\n{3,}/g, '\n\n');
  
  // Remove empty list items
  html = html.replace(/<li>\s*<\/li>/gi, '');
  
  // Clean up redundant bullet points in list items (common conversion issue)
  html = html.replace(/<li[^>]*>\s*[•\-*]\s*(.*?)<\/li>/gi, '<li>$1</li>');
  html = html.replace(/<li[^>]*>\s*\d+\.\s*(.*?)<\/li>/gi, '<li>$1</li>');
  
  // Merge consecutive lists of the same type
  html = html.replace(/<\/ul>\s*<ul[^>]*>/gi, '');
  html = html.replace(/<\/ol>\s*<ol[^>]*>/gi, '');
  
  // Add proper spacing around block elements
  html = html.replace(/(<\/(?:h[1-6]|p|ul|ol|blockquote|pre|table)>)\s*(<(?:h[1-6]|p|ul|ol|blockquote|pre|table))/gi, '$1\n\n$2');
  
  return html;
}

/**
 * Process citation markers in HTML content
 * Supports both [Source N] and [N] formats
 * When citationSources are provided, renders as clickable links/buttons with titles
 */
export function processCitationMarkers(
  html: string,
  _onCitationClick?: (index: number) => void,
  citationSources?: any[]
): string {
  if (!html) return '';

  try {
    // Helper function to render citation based on source data
    const renderCitation = (sourceNumber: string, index: number) => {
      const source = citationSources?.[index];

      // If we have source data with URL, render as clickable link with title
      if (source && source.url) {
        const title = source.title || `Source ${sourceNumber}`;
        return `<a href="${source.url}" target="_blank" rel="noopener noreferrer" class="citation-link" data-citation-index="${index}" data-source-number="${sourceNumber}">↗ ${title}</a>`;
      } else if (source && source.title) {
        // Source exists with title but no URL (e.g., document) - show as teal badge like links
        return `<span class="citation-document" data-citation-index="${index}" data-source-number="${sourceNumber}">\u2197 ${source.title}</span>`;
      }

      // Fallback: no source data - show styled number badge (no tooltip)
      return `<span
        class="citation-marker"
        data-citation-index="${index}"
        data-source-number="${sourceNumber}"
        style="
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background-color: #dbeafe;
          border: 1px solid #3b82f6;
          border-radius: 4px;
          padding: 0px 4px;
          color: #1e40af;
          font-weight: 600;
          cursor: pointer;
          font-size: 0.75rem;
          vertical-align: middle;
          line-height: 1;
          margin: 0 1px;
          min-width: 16px;
          height: 16px;
          transition: all 0.2s ease;
          text-align: center;
        "
        onmouseover="this.style.backgroundColor='#bfdbfe'; this.style.borderColor='#2563eb'; this.style.transform='translateY(-1px)'; this.style.boxShadow='0 2px 4px rgba(59, 130, 246, 0.3)';"
        onmouseout="this.style.backgroundColor='#dbeafe'; this.style.borderColor='#3b82f6'; this.style.transform='translateY(0)'; this.style.boxShadow='none';"
      >${sourceNumber}</span>`;
    };

    // Process [Source N] format first
    let processedHtml = html.replace(/\[Source\s*(\d+)\]/gi, (match, sourceNumber) => {
      const index = parseInt(sourceNumber, 10) - 1;
      return renderCitation(sourceNumber, index);
    });

    // Process legacy [N] format
    processedHtml = processedHtml.replace(/\[(\d+)\]/g, (match, sourceNumber) => {
      const index = parseInt(sourceNumber, 10) - 1;
      return renderCitation(sourceNumber, index);
    });

    return processedHtml;
  } catch (error) {
    console.error('[markdownRenderer] Error processing citation markers:', error);
    return html;
  }
}

/**
 * Main function to convert markdown to professional HTML with citation support
 */
export function renderMarkdownContent(
  markdown: string,
  options: {
    citationSources?: any[];
    onCitationClick?: (index: number) => void;
  } = {}
): string {
  if (!markdown) return '';

  try {
    // Convert markdown to HTML
    let html = convertMarkdownToHtml(markdown);

    // Process citation markers with sources for title rendering
    // This ensures citation markers are styled properly and show titles when sources are available
    html = processCitationMarkers(html, options.onCitationClick, options.citationSources);

    return html;
  } catch (error) {
    console.error('[markdownRenderer] Error in renderMarkdownContent:', error);
    return `<p>${markdown.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`;
  }
}

// Export for backward compatibility
export { renderMarkdownContent as processLLMResponse };